import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useSidebar } from '../../contexts/SidebarContext';

interface CommonPageHeaderProps {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  onRefresh?: () => void;
  refreshButtonText?: string;
  children?: React.ReactNode;
  actionButton?: React.ReactNode;
}

const CommonPageHeader: React.FC<CommonPageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  onRefresh,
  refreshButtonText = 'Frissítés',
  children,
  actionButton
}) => {
  const { isSidebarCollapsed } = useSidebar();
  return (
    <>
      {/* Desktop - Fixed header */}
        <div className={`bg-gradient-to-r from-gray-800 to-gray-900 h-24 items-center px-4 sm:px-6 lg:px-8 text-white border-b border-gray-700/30 fixed top-0 right-0 z-40 hidden md:flex transition-all duration-300 ${
          isSidebarCollapsed 
            ? 'left-16' 
            : 'left-48 lg:left-64'
        }`}>
        <div className="flex items-center justify-between w-full">
          {/* Left side - Title and subtitle */}
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <Icon className="w-6 h-6 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="text-lg lg:text-xl font-bold truncate">{title}</h1>
              <p className="text-gray-300 text-sm truncate">{subtitle}</p>
            </div>
          </div>
          
          {/* Right side - Actions */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {actionButton}
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="inline-flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-sm"
              >
                <ArrowPathIcon className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{refreshButtonText}</span>
              </button>
            )}
            {children}
          </div>
        </div>
      </div>

      {/* Mobile - Regular header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-3 sm:p-4 lg:p-6 text-white md:hidden mb-6">
        {/* Mobile layout - stacked */}
        <div className="space-y-3">
          {/* Title section */}
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="text-base font-bold truncate">{title}</h1>
              <p className="text-gray-300 text-xs truncate">{subtitle}</p>
            </div>
          </div>
          
          {/* Actions section */}
          {(actionButton || onRefresh || children) && (
            <div className="flex flex-wrap items-center gap-2">
              {actionButton}
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  className="inline-flex items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-md hover:bg-white/30 transition-colors text-xs whitespace-nowrap"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  <span className="hidden min-[400px]:inline">{refreshButtonText}</span>
                  <span className="min-[400px]:hidden">↻</span>
                </button>
              )}
              {children}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommonPageHeader;